// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";

function getDailyAggregationQuery(firestore, client_id, device_id, start_date, end_date) {
    /**
     * Get the daily aggregation data for a given device and date range
     * This query returns a document for each day in the date range.
     * The data then comes in the form of aggregation entries, which contain the aggregated data for each 5 minute interval.
     */
    // Calculate a broader query range
    // We'll query for documents that start up to 25 hours before end_date (assumng one doc roughly covers 24 hours)
    const extendedStartDate = new Date(start_date);
    extendedStartDate.setHours(extendedStartDate.getHours() - 25);
    
    return query(
        collection(firestore, "clients", client_id, "devices", device_id, "volumes", "volume_tracking", "aggregations_daily"),
        where("start_timestamp", ">=", extendedStartDate),
        where("start_timestamp", "<=", end_date),
        orderBy("start_timestamp", "asc")
    );
}

function getYearlyAggregationQuery(firestore, client_id, device_id, start_date, end_date) {
    /**
     * Get the yearly aggregation data for a given device and date range
     * This query returns a document for each year in the date range.
     * The data then comes in the form of aggregation entries, which contain the aggregated data for each day.
     */
    // Calculate a broader query range
    // We'll query for documents that start up to 1 year before end_date (assumng one doc roughly covers 1 year)
    const extendedStartDate = new Date(start_date);
    extendedStartDate.setFullYear(extendedStartDate.getFullYear() - 1);

    return query(
        collection(firestore, "clients", client_id, "devices", device_id, "volumes", "volume_tracking", "aggregations_yearly"),
        where("start_timestamp", ">=", extendedStartDate),
        where("start_timestamp", "<=", end_date),
        orderBy("start_timestamp", "asc")
    );
}

export function subscribeToAggregatedVolumeData(
    firestore,
    client_id,
    device_id,
    start_date,
    end_date,
    onUpdate,
    loadDetailed = true
) {
    /** 
     * Subscribe to the raw aggregated volume data for a given device and date range 
     * Return all entries that overlap with the date range
     * Call onUpdate with the data when it changes
     * Return an unsubscribe function
    */
    const volumeDataQuery = loadDetailed ? 
        getDailyAggregationQuery(firestore, client_id, device_id, start_date, end_date) : 
        getYearlyAggregationQuery(firestore, client_id, device_id, start_date, end_date);

    const unsub = onSnapshot(volumeDataQuery, (snapshot) => {
        let volumeData = [];
        snapshot.docs.forEach((doc) => {
            const data = doc.data();
            (data?.aggregation_entries || []).forEach((entry) => {
                entry["start_timestamp"] = entry["start_timestamp"].toDate();
                entry["end_timestamp"] = entry["end_timestamp"].toDate();
                if (entry.start_timestamp <= end_date && entry.end_timestamp >= start_date) {
                    volumeData.push(entry);
                }
            });
        });

        onUpdate(volumeData);
    });

    return unsub;
}

export function subscribeToAggregatedVolume(
    firestore,
    client_id,
    device_id,
    start_date,
    end_date,
    time_interval,
    onUpdate,
    loadDetailed = true
) {
    /**
     * Subscribe to aggregated and processed volume data for a given device and date range
     * Return all entries that overlap with the date range
     * Call onUpdate with the data when it changes
     * Return an unsubscribe function
    */
    
    function processVolumeData(volumeData) {
        /**
         * Process the raw aggregated volume data to create aggregated volume data
         * Parses the raw data into aggregation buckets of time_interval minutes within the given date range.
         * Data entries that fall between different intervals are split and weighted accordingly.
         * 
         * @param {Array} volumeData - The raw aggregated volume data
         * @returns {Array} The aggregated volume data
         */
        
        // Convert time_interval from minutes to milliseconds
        const intervalMs = time_interval * 60 * 1000;
        
        // Create intervals from start_date to end_date
        const intervals = [];
        let currentIntervalStart = new Date(start_date).getTime();
        const endTime = new Date(end_date).getTime();
        
        // Generate interval boundaries
        while (currentIntervalStart < endTime) {
            const intervalEnd = Math.min(currentIntervalStart + intervalMs, endTime);
            intervals.push({
                start: currentIntervalStart,
                end: intervalEnd,
                total_volume: 0
            });
            currentIntervalStart = intervalEnd;
        }
        
        // Process each volume data entry - optimized for sorted volumeData
        let currentIntervalIndex = 0;
        
        volumeData.forEach(entry => {
            const entryStartTime = new Date(entry.start_timestamp).getTime();
            const entryEndTime = new Date(entry.end_timestamp).getTime();
            const entryDuration = entryEndTime - entryStartTime;
            
            // Skip invalid entries
            if (entryDuration <= 0 || !entry.total_volume) return;
            
            // Calculate volume per millisecond for this entry
            const volumeRate = entry.total_volume / entryDuration;
            
            // Find the first interval that could overlap with this entry
            // We can skip intervals that end before this entry starts
            while (
                currentIntervalIndex < intervals.length && 
                intervals[currentIntervalIndex].end < entryStartTime
            ) {
                currentIntervalIndex++;
            }
            
            // If we've gone through all intervals, we're done with this entry
            if (currentIntervalIndex >= intervals.length) return;
            
            // Process all intervals that overlap with this entry
            let i = currentIntervalIndex;
            while (i < intervals.length && intervals[i].start < entryEndTime) {
                const interval = intervals[i];
                
                // Find overlapping time range
                const overlapStart = Math.max(interval.start, entryStartTime);
                const overlapEnd = Math.min(interval.end, entryEndTime);
                const overlapDuration = overlapEnd - overlapStart;
                
                // If there's an overlap, add the proportional volume to this interval
                if (overlapDuration > 0) {
                    const volumeContribution = volumeRate * overlapDuration;
                    interval.total_volume += volumeContribution;
                }
                
                i++;
                
                // If this interval starts after the entry ends, no need to check further intervals
                if (interval.start >= entryEndTime) break;
            }
        });
        
        // Format the result
        const result = intervals.map(interval => ({
            start_timestamp: new Date(interval.start),
            end_timestamp: new Date(interval.end),
            total_volume: interval.total_volume
        }));
        
        onUpdate(result);
    }

    return subscribeToAggregatedVolumeData(firestore, client_id, device_id, start_date, end_date, processVolumeData, loadDetailed);
}